// [Codex 2026-07-03] CAO PiP accepts app-wide quick annotation context.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { History, PanelRightOpen } from "lucide-react";
import { PromptBox } from "../../ui/chatgpt-prompt-input";
import {
  RepoChatComposer,
  RepoChatComposerSurface,
} from "../composer/RepoChatComposer";
import type {
  QuickAnnotateAnchor,
  QuickAnnotateContext,
} from "../../../lib/quick-annotate";
import "./cao-pip.css";

interface CaoPipComposerProps {
  open: boolean;
  onRestoreRail: () => void;
  quickAnnotateContext?: QuickAnnotateContext | null;
  onCloseQuickAnnotate?: () => void;
}

interface PipMessage {
  id: string;
  text: string;
}

export function CaoPipComposer({
  open,
  onRestoreRail,
  quickAnnotateContext,
  onCloseQuickAnnotate,
}: CaoPipComposerProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [shouldRender, setShouldRender] = useState(open);
  const [isVisible, setIsVisible] = useState(false);
  const [anchorRevision, setAnchorRevision] = useState(0);
  const [lastQuickAnnotateContext, setLastQuickAnnotateContext] =
    useState<QuickAnnotateContext | null>(quickAnnotateContext ?? null);
  const [draft, setDraft] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [messages, setMessages] = useState<PipMessage[]>([]);
  const [thinkHarder, setThinkHarder] = useState(false);

  useEffect(() => {
    let frame = 0;
    let closeTimer = 0;
    if (open) {
      setShouldRender(true);
      frame = window.requestAnimationFrame(() => setIsVisible(true));
      return () => window.cancelAnimationFrame(frame);
    }
    setIsVisible(false);
    closeTimer = window.setTimeout(() => setShouldRender(false), 190);
    return () => window.clearTimeout(closeTimer);
  }, [open]);

  useEffect(() => {
    if (!isVisible) return;
    const timer = window.setTimeout(() => {
      rootRef.current?.querySelector("textarea")?.focus();
    }, 80);
    return () => window.clearTimeout(timer);
  }, [isVisible, quickAnnotateContext]);

  useEffect(() => {
    if (!quickAnnotateContext?.anchor) return;
    const updateAnchor = () => setAnchorRevision((value) => value + 1);
    window.addEventListener("resize", updateAnchor);
    window.addEventListener("scroll", updateAnchor, true);
    return () => {
      window.removeEventListener("resize", updateAnchor);
      window.removeEventListener("scroll", updateAnchor, true);
    };
  }, [quickAnnotateContext?.anchor]);

  useEffect(() => {
    if (quickAnnotateContext) {
      setLastQuickAnnotateContext(quickAnnotateContext);
      return;
    }
    if (open) {
      setLastQuickAnnotateContext(null);
      return;
    }
    const timer = window.setTimeout(() => {
      setLastQuickAnnotateContext(null);
    }, 190);
    return () => window.clearTimeout(timer);
  }, [open, quickAnnotateContext]);

  useEffect(() => {
    if (!open || !quickAnnotateContext) return;
    setDraft("");
    setDrawerOpen(true);
  }, [open, quickAnnotateContext]);

  useEffect(() => {
    if (!open || !quickAnnotateContext) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onCloseQuickAnnotate?.();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onCloseQuickAnnotate, open, quickAnnotateContext]);

  const suggestions = useMemo(
    () => buildSuggestions(messages.at(-1)?.text),
    [messages],
  );
  const renderedQuickAnnotateContext =
    quickAnnotateContext ?? (open ? null : lastQuickAnnotateContext);
  const anchorStyle = useMemo(
    () => buildPipAnchorStyle(renderedQuickAnnotateContext?.anchor),
    [anchorRevision, renderedQuickAnnotateContext?.anchor],
  );

  const sendMessage = useCallback(
    (message: string) => {
      const trimmed = message.trim();
      if (!trimmed && !quickAnnotateContext?.selectedText) return;
      const outbound = quickAnnotateContext
        ? formatQuickAnnotateMessage(quickAnnotateContext, trimmed)
        : trimmed;
      window.dispatchEvent(
        new CustomEvent("fintheon:chat-sidebar-send", {
          detail: { message: outbound },
        }),
      );
      setMessages((current) => [
        ...current.slice(-5),
        { id: crypto.randomUUID(), text: outbound },
      ]);
      setDraft("");
    },
    [quickAnnotateContext],
  );

  if (!shouldRender) return null;

  return (
    <div
      ref={rootRef}
      className={anchorStyle ? "cao-pip cao-pip--anchored" : "cao-pip"}
      data-visible={isVisible ? "true" : "false"}
      style={anchorStyle}
      role="region"
      aria-label="Floating CAO chat"
    >
      <RepoChatComposerSurface
        open={drawerOpen || Boolean(quickAnnotateContext)}
        className="cao-pip__history"
        maxHeight="240px"
      >
        {renderedQuickAnnotateContext ? (
          <div className="cao-pip__quick-context">
            <div className="cao-pip__quick-head">
              <span>
                {renderedQuickAnnotateContext.mode === "selected-text"
                  ? "Quick annotate"
                  : "Quick instruction"}
              </span>
              <button type="button" onClick={onCloseQuickAnnotate}>
                Close
              </button>
            </div>
            <p>{renderedQuickAnnotateContext.label}</p>
            {renderedQuickAnnotateContext.path ||
            renderedQuickAnnotateContext.resourceId ? (
              <p className="cao-pip__quick-meta">
                {renderedQuickAnnotateContext.path ??
                  renderedQuickAnnotateContext.resourceId}
              </p>
            ) : null}
            {renderedQuickAnnotateContext.selectedText ? (
              <blockquote>
                {renderedQuickAnnotateContext.selectedText}
              </blockquote>
            ) : (
              <p>Type an instruction for Harper.</p>
            )}
          </div>
        ) : null}
        <div className="cao-pip__history-head">
          <span>Recent PiP turns</span>
          <button type="button" onClick={onRestoreRail}>
            <PanelRightOpen size={13} />
            <span>Rail</span>
          </button>
        </div>
        <div className="cao-pip__history-body">
          {messages.length === 0 ? (
            <p>No PiP messages yet.</p>
          ) : (
            messages.map((message) => <p key={message.id}>{message.text}</p>)
          )}
        </div>
      </RepoChatComposerSurface>

      {!renderedQuickAnnotateContext ? (
        <div className="cao-pip__suggestions" aria-label="CAO suggestions">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() =>
                suggestion === "Open rail"
                  ? onRestoreRail()
                  : setDraft(suggestion)
              }
            >
              {suggestion}
            </button>
          ))}
        </div>
      ) : null}

      <RepoChatComposer format="compact" maxWidth="29.4rem">
        <PromptBox
          value={draft}
          onValueChange={setDraft}
          onSend={sendMessage}
          placeholder={
            renderedQuickAnnotateContext
              ? "Tell Harper what to change..."
              : "Ask Harper..."
          }
          thinkHarder={thinkHarder}
          setThinkHarder={setThinkHarder}
          activeSkill={null}
          onSelectSkill={() => {}}
          showSkills={false}
          onToggleSkills={() => {}}
          compact
          hideThinkHarder
          showAttachSelector={false}
          draftKey="fintheon:cao-pip-draft"
          leftToolbarSlot={
            <button
              type="button"
              className="cao-pip__drawer-toggle"
              aria-label="Toggle PiP conversation drawer"
              onClick={() => setDrawerOpen((value) => !value)}
            >
              <History size={14} />
            </button>
          }
          workspaceSlot={
            <button
              type="button"
              className="cao-pip__restore"
              aria-label="Restore CAO rail"
              onClick={onRestoreRail}
            >
              <PanelRightOpen size={14} />
            </button>
          }
        />
      </RepoChatComposer>
    </div>
  );
}

const PIP_WIDTH_REM = 29.4;
const PIP_MIN_WIDTH_PX = 300;
const PIP_HEIGHT_PX = 360;
const PIP_GAP_PX = 14;
const PIP_MARGIN_PX = 12;

function buildPipAnchorStyle(
  anchor?: QuickAnnotateAnchor,
): CSSProperties | undefined {
  if (!anchor || typeof window === "undefined") return undefined;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const panelWidth = resolvePipWidth(viewportWidth);
  const panelHeight = Math.min(
    PIP_HEIGHT_PX,
    viewportHeight - PIP_MARGIN_PX * 2,
  );
  const hasRightRoom =
    anchor.right + PIP_GAP_PX + panelWidth <= viewportWidth - PIP_MARGIN_PX;
  const hasLeftRoom = anchor.left - PIP_GAP_PX - panelWidth >= PIP_MARGIN_PX;
  let left = anchor.right + PIP_GAP_PX;
  let top = anchor.top - 8;

  if (hasLeftRoom && !hasRightRoom) {
    left = anchor.left - PIP_GAP_PX - panelWidth;
  } else if (!hasRightRoom) {
    left = clamp(
      anchor.left + anchor.width / 2 - panelWidth / 2,
      PIP_MARGIN_PX,
      viewportWidth - panelWidth - PIP_MARGIN_PX,
    );
    const below = anchor.bottom + PIP_GAP_PX;
    const above = anchor.top - PIP_GAP_PX - panelHeight;
    top =
      below + panelHeight <= viewportHeight - PIP_MARGIN_PX
        ? below
        : Math.max(PIP_MARGIN_PX, above);
  }

  top = clamp(top, PIP_MARGIN_PX, viewportHeight - panelHeight - PIP_MARGIN_PX);
  left = clamp(left, PIP_MARGIN_PX, viewportWidth - panelWidth - PIP_MARGIN_PX);
  return {
    left: `${Math.round(left)}px`,
    top: `${Math.round(top)}px`,
  };
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

function resolvePipWidth(viewportWidth: number): number {
  const rootFontSize =
    Number.parseFloat(getComputedStyle(document.documentElement).fontSize) ||
    16;
  const remWidth = PIP_WIDTH_REM * rootFontSize;
  const cssWidth =
    viewportWidth <= 900
      ? viewportWidth - 32
      : Math.min(remWidth, viewportWidth - 320);
  return clamp(
    cssWidth,
    Math.min(PIP_MIN_WIDTH_PX, viewportWidth - PIP_MARGIN_PX * 2),
    viewportWidth - PIP_MARGIN_PX * 2,
  );
}

function buildSuggestions(lastMessage?: string): string[] {
  const suggestions = [
    "Review top RiskFlow signal",
    "Check ER tilt",
    "Summarize next desk action",
  ];
  if (lastMessage) suggestions.unshift(`Continue: ${lastMessage.slice(0, 42)}`);
  suggestions.push("Open rail");
  return suggestions;
}

function formatQuickAnnotateMessage(
  context: QuickAnnotateContext,
  instruction: string,
): string {
  const parts = ["[Quick Annotate]"];
  parts.push(`Surface: ${context.surface}`);
  parts.push(`Context: ${context.label}`);
  if (context.resourceId) parts.push(`Resource: ${context.resourceId}`);
  if (context.path) parts.push(`Path: ${context.path}`);
  if (context.url) parts.push(`URL: ${context.url}`);
  if (context.selectedText) {
    parts.push("");
    parts.push("Selected text:");
    parts.push(context.selectedText);
  }
  if (instruction) {
    parts.push("");
    parts.push("Instruction:");
    parts.push(instruction);
  }
  return parts.join("\n");
}
